import { useState, useEffect } from 'react';
import type { AuditFormData } from '@/pages/audits/CreateAudit';
import { dataService } from '@/services/data-service';
import type { Asset } from '@/types/asset-audit';
import { AuditPriority } from '@/types/asset-audit';
import type { Location, Category } from '@/types/settings';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { UserAvatar } from '@/components/audits/UserAvatar';
import { 
  Calendar, 
  Package, 
  Users, 
  Mail,
  Repeat,
  Building2,
  FolderTree,
  Globe
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useUsers } from '@/hooks/use-users';
import { useTeams } from '@/hooks/use-teams';
import { getIconByName } from '@/lib/team-icons';

interface ReviewStepProps {
  formData: AuditFormData;
  onEdit?: (step: number) => void;
}

export function ReviewStep({ formData }: Readonly<ReviewStepProps>) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  
  const usersHook = useUsers();
  const teamsHook = useTeams();

  useEffect(() => {
    loadData();
  }, [formData.scopeType, formData.locationIds, formData.categoryIds, formData.assetIds]);

  const loadData = async () => {
    try {
      const [assetsResult, locationsData, categoriesData] = await Promise.all([
        dataService.getAssets({ page: 1, pageSize: 10000 }),
        dataService.getLocations(),
        dataService.getCategories(),
        usersHook.loadUsers(),
        teamsHook.loadTeams(),
      ]);

      const assetData = assetsResult.data ?? [];
      
      // Get selected assets based on scope type
      let selectedAssets: Asset[] = [];
      if (formData.scopeType === 'all') {
        selectedAssets = assetData;
      } else if (formData.scopeType === 'location') {
        selectedAssets = assetData.filter((a: Asset) => 
          a.locationId && formData.locationIds.includes(a.locationId)
        );
      } else if (formData.scopeType === 'category') {
        selectedAssets = assetData.filter((a: Asset) => 
          a.categoryId && formData.categoryIds.includes(a.categoryId)
        );
      } else if (formData.scopeType === 'custom') {
        // Convert both to strings for comparison to handle type mismatches
        const assetIdStrings = formData.assetIds.map(id => String(id));
        selectedAssets = assetData.filter((a: Asset) => assetIdStrings.includes(String(a.id)));
      }
      
      setAssets(selectedAssets);
      setLocations(locationsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const getScopeDescription = () => {
    if (formData.scopeType === 'all') {
      return { icon: Globe, text: 'All Assets', detail: 'All assets in the system' };
    } else if (formData.scopeType === 'location') {
      const selectedLocations = locations.filter(l => formData.locationIds.includes(l.id));
      return { 
        icon: Building2, 
        text: 'By Location', 
        detail: selectedLocations.map(l => l.name).join(', ') || 'No locations selected'
      };
    } else if (formData.scopeType === 'category') {
      const selectedCategories = categories.filter(c => formData.categoryIds.includes(c.id));
      return { 
        icon: FolderTree, 
        text: 'By Category', 
        detail: selectedCategories.map(c => c.description || c.code).join(', ') || 'No categories selected'
      };
    } else {
      return { icon: Package, text: 'Custom List', detail: 'Manually selected assets' };
    }
  };

  const scopeInfo = getScopeDescription();
  const ScopeIcon = scopeInfo.icon;

  return (
    <Card className="bg-card border rounded-lg shadow-sm">
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Review & Create</h3>
            <p className="text-sm text-muted-foreground">
              Review all details before creating the audit.
            </p>
          </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Column - Asset Information (60%) */}
        <div className="lg:col-span-3 space-y-6">
          {/* Basic Information */}
          <div className="border rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-semibold">Audit Details</h4>
            <div className="space-y-2 text-sm">
              <div>
                <p className="text-muted-foreground">Name</p>
                <p className="font-medium">{formData.name || formData.title}</p>
              </div>
              {formData.description && (
                <div>
                  <p className="text-muted-foreground">Description</p>
                  <p className="font-medium">{formData.description}</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground">Type</p>
                <Badge variant="secondary" className="capitalize">{formData.type}</Badge>
              </div>
              <div>
                <p className="text-muted-foreground">Priority</p>
                <Badge 
                  variant="secondary" 
                  className={cn(
                    "capitalize",
                    (formData.priority === AuditPriority.High || formData.priority === AuditPriority.Critical) && "bg-red-100 text-red-800",
                    formData.priority === AuditPriority.Medium && "bg-yellow-100 text-yellow-800",
                    formData.priority === AuditPriority.Low && "bg-green-100 text-green-800"
                  )}
                >
                  {formData.priority}
                </Badge>
              </div>
            </div>
          </div>

          {/* Scope Information */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <ScopeIcon className="h-4 w-4" />
              <h4 className="text-sm font-semibold">Scope</h4>
            </div>
            <div className="space-y-2 text-sm">
              <div>
                <p className="text-muted-foreground">Scope Type</p>
                <p className="font-medium">{scopeInfo.text}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Selection</p>
                <p className="font-medium">{scopeInfo.detail}</p>
              </div>
            </div>
          </div>

          {/* Assets List */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                <h4 className="text-sm font-semibold">Assets to Audit</h4>
              </div>
              <Badge variant="secondary">{assets.length} assets</Badge>
            </div>
            {assets.length > 0 ? (
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {assets.map((asset) => (
                  <div 
                    key={asset.id} 
                    className="flex items-center justify-between text-sm py-2 px-2 hover:bg-muted/50 rounded border-b last:border-0"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{asset.name}</p>
                      <p className="text-xs text-muted-foreground">{asset.assetTag}</p>
                    </div>
                    <Badge variant="outline" className="ml-2 shrink-0">{asset.category}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No assets match the selected scope</p>
            )}
          </div>
        </div>

        {/* Right Column - Schedule & Team (40%) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Schedule */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <h4 className="text-sm font-semibold">Schedule</h4>
            </div>
            <div className="space-y-3 text-sm">
              {formData.scheduleType !== 'immediate' && (
                <div>
                  <p className="text-muted-foreground">Start Date</p>
                  <p className="font-medium">
                    {formData.startDate ? format(new Date(formData.startDate), 'MMM dd, yyyy') : 'Not set'}
                  </p>
                </div>
              )}
              {formData.scheduleType === 'immediate' && (
                <div>
                  <p className="text-muted-foreground">Start Date</p>
                  <p className="font-medium">Immediately</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground">Due Date</p>
                <p className="font-medium">
                  {formData.dueDate ? format(new Date(formData.dueDate), 'MMM dd, yyyy') : 'Not set'}
                </p>
              </div>
              {formData.isRecurring && (
                <>
                  <div className="pt-2 border-t">
                    <div className="flex items-center gap-2 mb-2">
                      <Repeat className="h-3 w-3 text-muted-foreground" />
                      <p className="text-muted-foreground">Recurring</p>
                    </div>
                    <p className="font-medium capitalize">
                      {formData.recurringFrequency || formData.recurrencePattern}
                    </p>
                  </div>
                  {(formData.recurringEndDate || formData.recurrenceEndDate) && (
                    <div>
                      <p className="text-muted-foreground">Ends</p>
                      <p className="font-medium">
                        {format(
                          new Date(formData.recurringEndDate || formData.recurrenceEndDate!), 
                          'MMM dd, yyyy'
                        )}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Assigned Team */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <h4 className="text-sm font-semibold">Assigned Team</h4>
              </div>
              <Badge variant="secondary">{formData.assigneeIds.length}</Badge>
            </div>
            {formData.assigneeIds.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {/* Show selected teams if any */}
                {formData.selectedTeamIds && formData.selectedTeamIds.length > 0 && (
                  <div className="space-y-2">
                    {formData.selectedTeamIds.map((teamId) => {
                      const team = teamsHook.teams.find(t => t.id === teamId);
                      if (!team) return null;
                      
                      const teamMembers = usersHook.users.filter(user => {
                        if (Array.isArray(user.teams)) {
                          return user.teams.some(t => 
                            typeof t === 'string' ? t === teamId : t.teamId === teamId
                          );
                        }
                        return false;
                      });
                      
                      const IconComponent = getIconByName(team.icon);
                      
                      return (
                        <div key={teamId} className="border rounded-lg p-3 bg-muted/30">
                          <div className="flex items-center gap-2 mb-2">
                            <div 
                              className="w-6 h-6 rounded flex items-center justify-center"
                              style={{ backgroundColor: team.color }}
                            >
                              {IconComponent ? (
                                <IconComponent className="h-3.5 w-3.5 text-white" />
                              ) : (
                                <Users className="h-3.5 w-3.5 text-white" />
                              )}
                            </div>
                            <span className="text-sm font-medium">{team.name}</span>
                            <Badge variant="outline" className="text-xs ml-auto">
                              {teamMembers.length} {teamMembers.length === 1 ? 'member' : 'members'}
                            </Badge>
                          </div>
                          <div className="space-y-1 ml-8">
                            {teamMembers.map((user) => (
                              <div key={user.id} className="flex items-center gap-2 p-1.5 bg-background rounded">
                                <UserAvatar user={{ id: user.id, name: user.name, email: user.email }} size="xs" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium truncate">{user.name}</p>
                                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Show individual users if no teams selected */}
                {(!formData.selectedTeamIds || formData.selectedTeamIds.length === 0) && (
                  <div className="space-y-2">
                    {formData.assigneeIds.map((userId) => {
                      const user = usersHook.users.find(u => u.id === userId);
                      return user ? (
                        <div key={user.id} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                          <UserAvatar user={{ id: user.id, name: user.name, email: user.email }} size="sm" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{user.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                          </div>
                        </div>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No team members assigned</p>
            )}
          </div>

          {/* Notification Settings */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <h4 className="text-sm font-semibold">Notifications</h4>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <div className={cn(
                  "h-4 w-4 rounded-sm border flex items-center justify-center mt-0.5",
                  formData.notifyAssignees ? "bg-primary border-primary" : "border-muted-foreground"
                )}>
                  {formData.notifyAssignees && (
                    <svg className="h-3 w-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className="font-medium">Notify on creation</p>
                  <p className="text-xs text-muted-foreground">Send email to assignees</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className={cn(
                  "h-4 w-4 rounded-sm border flex items-center justify-center mt-0.5",
                  formData.notifyOnCompletion ? "bg-primary border-primary" : "border-muted-foreground"
                )}>
                  {formData.notifyOnCompletion && (
                    <svg className="h-3 w-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className="font-medium">Notify on completion</p>
                  <p className="text-xs text-muted-foreground">Alert when audit is done</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className={cn(
                  "h-4 w-4 rounded-sm border flex items-center justify-center mt-0.5",
                  formData.allowReassignment ? "bg-primary border-primary" : "border-muted-foreground"
                )}>
                  {formData.allowReassignment && (
                    <svg className="h-3 w-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className="font-medium">Allow reassignment</p>
                  <p className="text-xs text-muted-foreground">Team can reassign assets</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
        </div>
      </CardContent>
    </Card>
  );
}
